/**
 * Local createIcons implementation that avoids importing from the lucide barrel file.
 * Importing from 'lucide' pulls in all 1,600+ icons due to barrel re-exports.
 * This file imports only the utilities needed (createElement, defaultAttributes)
 * from their individual module paths.
 *
 * Based on lucide v0.542.0 createIcons / replaceElement.
 */
import createElement from 'lucide/dist/esm/createElement.mjs';
import defaultAttributes from 'lucide/dist/esm/defaultAttributes.mjs';

const getAttrs = (element) => Array.from(element.attributes).reduce((attrs, attr) => {
    attrs[attr.name] = attr.value;
    return attrs;
}, {});

const getClassNames = (attrs) => {
    if (typeof attrs === 'string') return attrs;
    if (!attrs || !attrs.class) return '';
    if (typeof attrs.class === 'string') return attrs.class.split(' ');
    if (Array.isArray(attrs.class)) return attrs.class;
    return '';
};

const combineClassNames = (arrayOfClassnames) =>
    arrayOfClassnames
        .flatMap(getClassNames)
        .map(c => c.trim())
        .filter(Boolean)
        .filter((v, i, a) => a.indexOf(v) === i)
        .join(' ');

const toPascalCase = (string) =>
    string.replace(/(\w)(\w*)(_|-|\s*)/g, (g0, g1, g2) => g1.toUpperCase() + g2.toLowerCase());

function replaceElement(element, { nameAttr, icons, attrs }) {
    const iconName = element.getAttribute(nameAttr);
    if (iconName == null) return;

    const ComponentName = toPascalCase(iconName);
    const iconNode = icons[ComponentName];
    if (!iconNode) {
        console.warn(`${element.outerHTML} icon name was not found in the provided icons object.`);
        return;
    }

    const elementAttrs = getAttrs(element);
    const iconAttrs = { ...defaultAttributes, 'data-lucide': iconName, ...attrs, ...elementAttrs };
    const classNames = combineClassNames(['lucide', `lucide-${iconName}`, elementAttrs, attrs]);
    if (classNames) {
        Object.assign(iconAttrs, { class: classNames });
    }

    const svgElement = createElement(iconNode, iconAttrs);
    return element.parentNode?.replaceChild(svgElement, element);
}

export function createIcons({ icons = {}, nameAttr = 'data-lucide', attrs = {}, root = document } = {}) {
    if (!Object.values(icons).length) {
        throw new Error('Please provide an icons object.');
    }

    const elements = root.querySelectorAll(`[${nameAttr}]`);
    Array.from(elements).forEach(element => replaceElement(element, { nameAttr, icons, attrs }));
}
